import React, { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { ROLES } from '../constants/roles.js';
import { MENU_ITEMS, MOBILE_BOTTOM_NAV_LIMIT } from './menu-data.js';
import CopilotDrawer from '../components/copilot/CopilotDrawer.jsx';
import { BRAND } from '../brand.js';
import { BrandLockup, BrandStripe } from '../components/brand/BrandMark.jsx';
import './web-shell.scss';

/**
 * @param {{ children?: React.ReactNode }} props - `children` overrides the
 *   routed <Outlet /> when a layout needs to wrap explicit content.
 *
 * The chrome shared by every web layout in the design: a fixed-top brand
 * navbar with the primary menu, a scrollable mobile bottom nav, and a footer.
 *
 * All four raw layouts (_default-web-layout, _conversational-chat-logs-layout,
 * _map-property-capture-layout, _matching-scoring-panel-layout) are byte-for-byte
 * identical apart from which extra stylesheets they pull in, so they are all
 * thin wrappers over this component rather than four near-duplicate copies.
 *
 * The design's preview-only scripting (error overlay, section-selection
 * outlines, postMessage navigation to the parent frame) is intentionally not
 * carried over — it exists to serve the Design Studio iframe preview, not the
 * running app. Menu clicks are real router navigations here.
 */
export default function WebShell({ children }) {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [copilotOpen, setCopilotOpen] = useState(false);
  const greetingName = user?.name || user?.email || 'User';
  const bottomNavItems = MENU_ITEMS.slice(0, MOBILE_BOTTOM_NAV_LIMIT);
  const isAdmin = user?.role === ROLES.ADMIN;

  /*
    The avatar was <img src="https://placehold.co/30">: a third-party image
    fetched on every authenticated page load, which leaks who is using the app
    and when to a host that has nothing to do with it, and shows a grey box if
    that host is slow, down, or blocked. There is no uploaded-avatar feature to
    stand in for, so it renders the user's own initial instead — real data,
    no request.
  */
  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary text-light fixed-top web-top-nav">
        <div className="container-fluid">
          {/* The brand is the only route back to the overview — it used to point
              at /listings, so on a phone (where the top menu was hidden) the
              landing page became unreachable the moment you left it. */}
          <Link className="navbar-brand web-nav-brand" to="/" title="Overview">
            <BrandLockup size={30} />
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon" />
          </button>

          {/*
            The DesignStudio v4 nav is a flat row of all eleven destinations
            (menu-config.json), not grouped dropdowns — so it is rendered flat
            here to match the design.
          */}
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav">
              {MENU_ITEMS.map((item) => (
                <li className="nav-item" key={item.path}>
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link px-2${isActive ? ' active fw-bold' : ''}`
                    }
                    to={item.path}
                  >
                    <i className={`${item.icon} me-1`} aria-hidden="true" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="ms-auto d-flex align-items-center gap-2">
            {isAuthenticated && (
              // Read-only copilot. Sits beside the user menu, available on every
              // page — enabling the LLM section is what turns it on.
              <button
                type="button"
                className="copilot-trigger"
                onClick={() => setCopilotOpen(true)}
                aria-label={`Ask ${BRAND.name}`}
              >
                <i className="fa fa-wand-magic-sparkles" aria-hidden="true" />
                <span>Ask {BRAND.name}</span>
              </button>
            )}
            {isAuthenticated ? (
              <div className="dropdown">
                <button
                  className="btn btn-link d-flex align-items-center text-white text-decoration-none dropdown-toggle p-0"
                  type="button"
                  id="webNavUserMenu"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="me-2 web-nav-greeting">Hello, {greetingName}!</span>
                  <span className="rounded-circle web-nav-avatar" aria-hidden="true">
                    {initial}
                  </span>
                </button>

                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="webNavUserMenu">
                  <li>
                    <span className="dropdown-item-text small text-muted">
                      Signed in as <strong>{user?.role}</strong>
                    </span>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>

                  {isAdmin && (
                    <>
                      <li>
                        <Link className="dropdown-item" to="/admin/users">
                          <i className="fa fa-users-cog me-2" aria-hidden="true" />
                          User management
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/admin/settings">
                          <i className="fa fa-gear me-2" aria-hidden="true" />
                          Settings
                        </Link>
                      </li>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                    </>
                  )}

                  <li>
                    <Link className="dropdown-item" to="/help">
                      <i className="fa fa-circle-question me-2" aria-hidden="true" />
                      Help &amp; user guide
                    </Link>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>

                  <li>
                    <button className="dropdown-item" type="button" onClick={handleLogout}>
                      <i className="fa fa-right-from-bracket me-2" aria-hidden="true" />
                      Sign out
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <Link className="btn btn-sm btn-light fw-semibold" to="/login">
                Login
              </Link>
            )}
          </div>
        </div>
        <BrandStripe />
      </nav>

      {isAuthenticated && (
        <CopilotDrawer open={copilotOpen} onClose={() => setCopilotOpen(false)} />
      )}

      <div className="web-shell">
        <main>{children ?? <Outlet />}</main>

        {/*
          The footer was an empty spacer. The Help link lives here because it is
          the one piece of chrome every layout already shares, so a single link
          reaches every page — and because a user guide belongs where it is found
          when needed rather than competing for room in the primary nav.
        */}
        <footer className="web-shell-footer pt-4 mt-5">
          <div className="container">
            <hr className="mb-3" />
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2">
              <span className="text-muted small">{BRAND.name}</span>
              <Link className="text-muted small text-decoration-none" to="/help">
                <i className="fa fa-circle-question me-1" aria-hidden="true" />
                Help &amp; user guide
              </Link>
            </div>
          </div>
        </footer>
      </div>

      <nav className="mobile-bottom-nav" aria-label="Mobile bottom navigation">
        <div className="mobile-bottom-nav-scroll">
          {bottomNavItems.map((item) => (
            <div className="nav-item" key={item.path}>
              <NavLink
                className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                to={item.path}
                title={item.label}
              >
                <i className={item.icon} aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            </div>
          ))}
        </div>
      </nav>
    </>
  );
}
